import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useCreateBem, type BemTipo, type CreateBemInput } from '@/lib/hooks/bens';

interface FormValues {
  descricao: string;
  tipo: BemTipo | '';
  valor: string;
  dataAquisicao: string;
  estado: string;
  localizacao: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULTS: FormValues = {
  descricao: '',
  tipo: '',
  valor: '',
  dataAquisicao: '',
  estado: '',
  localizacao: '',
};

const TIPOS: { value: BemTipo; label: string }[] = [
  { value: 'MOVEL', label: 'Móvel' },
  { value: 'IMOVEL', label: 'Imóvel' },
  { value: 'VEICULO', label: 'Veículo' },
];

export function BemDrawer({ workspaceId, open, onOpenChange }: Props) {
  const createMut = useCreateBem(workspaceId);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) reset(DEFAULTS);
  }, [open, reset]);

  async function onSubmit(values: FormValues) {
    if (!values.tipo) {
      toast.error('Selecione o tipo do bem.');
      return;
    }
    const payload: CreateBemInput = {
      descricao: values.descricao,
      tipo: values.tipo,
      valor: values.valor.replace(',', '.'),
      dataAquisicao: values.dataAquisicao,
    };
    if (values.estado) payload.estado = values.estado;
    if (values.localizacao) payload.localizacao = values.localizacao;
    try {
      await createMut.mutateAsync(payload);
      toast.success('Bem cadastrado.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cadastrar bem.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Novo lançamento"
            title="Cadastrar bem"
            description="Móvel, imóvel ou veículo vinculado ao convênio."
          />
          <SheetBody className="space-y-5">
            <div>
              <Label htmlFor="descricao" className="mb-1.5 block">Descrição</Label>
              <Input
                id="descricao"
                placeholder="Ex.: Notebook Dell Latitude 5430"
                {...register('descricao', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.descricao && (
                <p className="text-[11px] text-warn mt-1">{errors.descricao.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Tipo</Label>
                <Controller
                  control={control}
                  name="tipo"
                  rules={{ required: 'Obrigatório' }}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.tipo && (
                  <p className="text-[11px] text-warn mt-1">{errors.tipo.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="dataAquisicao" className="mb-1.5 block">Data de aquisição</Label>
                <Input
                  id="dataAquisicao"
                  type="date"
                  {...register('dataAquisicao', { required: 'Obrigatório' })}
                />
                {errors.dataAquisicao && (
                  <p className="text-[11px] text-warn mt-1">{errors.dataAquisicao.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="valor" className="mb-1.5 block">Valor (R$)</Label>
              <Input
                id="valor"
                inputMode="decimal"
                placeholder="0.00"
                {...register('valor', {
                  required: 'Obrigatório',
                  pattern: { value: /^\d+([.,]\d{1,2})?$/, message: 'Valor inválido' },
                })}
              />
              {errors.valor && <p className="text-[11px] text-warn mt-1">{errors.valor.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="estado" className="mb-1.5 block">Estado de conservação</Label>
                <Input id="estado" placeholder="Ex.: Bom" {...register('estado')} />
              </div>
              <div>
                <Label htmlFor="localizacao" className="mb-1.5 block">Localização</Label>
                <Input id="localizacao" placeholder="Ex.: Sede" {...register('localizacao')} />
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar bem'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
